/**
 * Optimistic-concurrency (OCC) stale-write "Reload" resolution marker.
 *
 * When a guarded record save hits a 409 Conflict (the record changed after it
 * was opened) the Console shows a conflict dialog with Reload / Overwrite. On
 * Reload, `updateResource` (Resources.saga) has already stored the server's
 * current record, but it cannot touch the edit form — so it rejects the save
 * promise with a TAGGED value produced by `makeStaleWriteReloadRejection`.
 * `ResourcesSingle` detects that tag with `isStaleWriteReloadRejection` and
 * finishes the reload on the form side (discard local edits, realign the OCC
 * baseline token) instead of treating it as a genuine save failure.
 *
 * Keeping the tag string and shape in one place means the saga producer and the
 * form consumer cannot silently drift apart (a bare string literal duplicated in
 * two files is exactly how such a contract regresses).
 * See operator-task-fe-record-conflict-dialog-reload-does-not-reload.
 */

export const STALE_WRITE_RESOLUTION_RELOAD = "reload";

export interface StaleWriteReloadRejection {
    staleWriteResolution: typeof STALE_WRITE_RESOLUTION_RELOAD;
    conflict: unknown;
}

/** Build the tagged rejection the reload path rejects the save promise with. */
export const makeStaleWriteReloadRejection = (
    conflict: unknown,
): StaleWriteReloadRejection => ({
    staleWriteResolution: STALE_WRITE_RESOLUTION_RELOAD,
    conflict,
});

/**
 * True iff `error` is the reload-resolution tag — i.e. a resolved user choice the
 * form must complete, not a genuine save failure to rethrow.
 */
export const isStaleWriteReloadRejection = (
    error: unknown,
): error is StaleWriteReloadRejection =>
    !!error &&
    typeof error === "object" &&
    (error as { staleWriteResolution?: unknown }).staleWriteResolution ===
        STALE_WRITE_RESOLUTION_RELOAD;
